import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useTheme, alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import { FaMinimize, FaMaximize } from 'react-icons/fa6'
import defaultTexts from '../../../texts/texts'
import constants from '../../../config/constants'
import { request } from '../../../_network/request'
import { serviceConfigsURLs, fileManagerURLs } from '../../../_network/apiUrls'
import { uiDomainInitial, uiCertificateInitial } from '../_common/userInterfaceConstants'
import { isWildcardMatch } from '../_helpers/rsaCertificates'
import { getFormattedDateTime, getIsoDateTime } from '../_helpers/dates'
import CustomBackdrop from '../../_common/CustomBackdrop'
import CustomDialog from '../../_common/CustomDialog'
import AdminUiView from './AdminUiView'
import AdminUiEdit from './AdminUiEdit'

const AdminUiWrapper = (props) => {
  const { adminUiService, setFetchServices } = props
  const theme = useTheme()

  const user_role = useSelector(state => state.profile.user_role)
  const editPermission = user_role === 'superadmin' || user_role === 'owner' || user_role === 'admin'

  const [expanded, setExpanded] = useState(true)
  const [editMode, setEditMode] = useState(false)

  const [adminUiDomain, setAdminUiDomain] = useState([{ ...uiDomainInitial }])
  const [adminUiCertificateJobs, setAdminUiCertificateJobs] = useState([])
  const [adminUiCertificates, setAdminUiCertificates] = useState([{ ...uiCertificateInitial }])
  const [adminUiSiteKey, setAdminUiSiteKey] = useState('')
  const [adminUiSecretKey, setAdminUiSecretKey] = useState('')
  const [adminUiLogo, setAdminUiLogo] = useState(null)
  const [adminUiCurrentLogo, setAdminUiCurrentLogo] = useState(null)
  const [adminUiPrimaryColor, setAdminUiPrimaryColor] = useState(theme.palette.oriolesOrange)
  const [adminUiSecondaryColor, setAdminUiSecondaryColor] = useState(theme.palette.mayGreen)

  const [domainsWithCertififcates, setDomainsWithCertificates] = useState([])
  const [certificateAction, setCertificateAction] = useState('')
  const [errors, setErrors] = useState([])
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const wrapperStyle = {
    background: 'white',
    borderRadius: '50px 20px 20px 20px',
    boxShadow: '2px 2px 2px rgba(0, 0, 0, 0.25)',
    marginBottom: '40px',
  }

  const headStyle = {
    width: '100%',
    height: '60px',
  }

  const bodyStyle = {
    padding: '20px',
  }

  const labelStyle = {
    height: '60px',
    background: theme.palette.oxfordBlue,
    color: 'white',
    fontSize: '20px',
    fontWeight: '500',
    borderRadius: '50px 0px',
    padding: '0px 40px',    
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const editButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.vodka,
    fontSize: '18px',
    height: '40px',
    borderRadius: '20px',
    padding: '0px 30px',
    textTransform: 'capitalize',
    marginTop: '20px',

    '&:hover': {
      background: alpha(theme.palette.vodka, 0.8),
    },
  }

  const saveButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.greenSheen,
    fontSize: '18px',
    height: '40px',
    borderRadius: '20px',
    padding: '0px 30px',
    textTransform: 'capitalize',
    marginTop: '20px',

    '&:hover': {
      background: alpha(theme.palette.greenSheen, 0.8),
    },
  }

  const removeSpacesAndSpecialChars = (str) => {
    return str.replace(/[\s~`!@#$%^&*(){}[\];:"'<,>?/\\|+=-]/g, '')
  }

  const handleErrorDialogOpen = () => {
    setErrorDialogOpen(true)
  }

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false)
  }

  const getDomainListFromCertificate = (service_domains, certificate_domain) => {
    let result = []

    service_domains.forEach(d => {
      const is_wildcard_match = isWildcardMatch(certificate_domain, d)
      if (is_wildcard_match && !result.includes(d)) {
        result.push(d)
      }
    })

    return result
  }

  const getDomainListFromJobId = (job_id) => {
    let result = []
    const filtered_jobs = adminUiCertificateJobs.filter(item => item.job_id === job_id)

    filtered_jobs.forEach(item => {
      result.push(item.domain)
    })

    return result
  }

  const deleteFileRequest = async (file_url) => {
    const url = constants.core_service_domain + '/' + fileManagerURLs.getDeleteFile
    const form_data = new FormData()
    form_data.append('file_url', file_url)
    const request_data = { url, method: 'DELETE', form_data }
    await request(request_data)
  }

  const verifyDomain = async ({ showLoading }) => {
    if (showLoading) {
      setIsLoading(true)
    }
    
    let result = true
    let updated_domains = []

    for (let index = 0; index < adminUiDomain.length; index++) {
      const d = adminUiDomain[index]
      let new_status = ''

      if (!d.domain_name) {
        result = false
      }

      if (d.domain_name && d.status !== 'verified') {
        const url = constants.core_service_domain + '/' + serviceConfigsURLs.getClientServiceDnsResolver

        const postData = {
          domain_name: d.domain_name,
          subdomain_prefix: d.subdomain_prefix,
          type: 'admin',
        }

        const request_data = { url, method: 'POST', data: postData }    

        const response = await request(request_data)
        
        if (response && response.status_code < 400) {
          new_status = 'verified'
        } else {
          new_status = 'not verified'
          result = false

          if (response && response.error) {
            const error = JSON.parse(response.error)
            if (error && error.detail) {
              new_status = error.detail
            }
          }
        }

        updated_domains.push({ ...d, status: new_status })
      } else {
        updated_domains.push(d)
      }
    }

    if (showLoading) {
      setIsLoading(false)
    }
    
    setAdminUiDomain(updated_domains)
    
    return {
      result,
      domains: updated_domains,
    }
  }

  const validateAdminFrontendForm = () => {
    let errors_list = []

    adminUiDomain.forEach((item, index) => {
      const domain = item.subdomain_prefix ? item.subdomain_prefix + '.' + item.domain_name : item.domain_name

      if (!domain) {
        errors_list.push({
          title: defaultTexts.openStackAdminUi + ' - ' + defaultTexts.userInterfaceDomain,
          field_key: 'admin-domain_name-' + index,
        })
      } else if (!domainsWithCertififcates.includes(domain)) {
        errors_list.push({
          title: defaultTexts.noValidCertificateForDomain + ' - ' + domain,
        })
      }
    })

    if (!adminUiSiteKey) {
      errors_list.push({
        title: defaultTexts.openStackAdminUi + ' - ' + defaultTexts.siteKey,
        field_key: 'adminUiSiteKey',
      })
    }

    if (!adminUiSecretKey) {
      errors_list.push({
        title: defaultTexts.openStackAdminUi + ' - ' + defaultTexts.secretKey,
        field_key: 'adminUiSecretKey',
      })
    }

    if (adminUiLogo) {
      if (adminUiLogo.type && adminUiLogo.type !== 'image/png' && adminUiLogo.type !== 'image/jpeg') {
        errors_list.push({
          title: defaultTexts.openStackAdminUi + ' - ' + defaultTexts.uploadLogosFile,
        })
      }
    }

    return errors_list
  }

  const validateFormFields = () => {
    let errors_list = []

    const frontend_admin_form_errors = validateAdminFrontendForm()
    errors_list = [...errors_list, ...frontend_admin_form_errors]

    setErrors(errors_list)
    
    return errors_list.length === 0 ? true : false
  }

  const validateForm = async () => {
    const form_fields_valid = validateFormFields()
    let admin_domains_verified = { result: false, domains: adminUiDomain }

    if (form_fields_valid) {
      admin_domains_verified = await verifyDomain({ showLoading: false })
    }
 
    if (form_fields_valid && !admin_domains_verified.result) {
      setErrors(prev => [...prev, { title: defaultTexts.adminUiDomain + ' ' + defaultTexts.notVerified }])
    }

    const result = (form_fields_valid && admin_domains_verified.result)

    return {
      result,
      admin_ui_domains: admin_domains_verified.domains,
    }
  }

  const handleClickEdit = () => {
    setEditMode(true)
  }

  const uploadFileRequest = async (file_to_upload, url_key) => {
    const url = constants.core_service_domain + '/' + fileManagerURLs[url_key]

    const form_data = new FormData()
    form_data.append('file', file_to_upload, removeSpacesAndSpecialChars(file_to_upload.name))

    const request_data = { url, method: 'POST', form_data }

    const response = await request(request_data)

    if (response && response.status_code && response.status_code < 400 && response.data && response.data.url) {
      return response.data.url
    }
  }

  const clientServiceConfigsRequest = async (postData) => {
    const url = constants.core_service_domain + '/' + serviceConfigsURLs.getClientServiceConfigs + '/' + adminUiService.id
    const request_data = { url, method: 'PUT', data: postData }
    await request(request_data)
  }

  const clientCertificateConfigsRequest = async ({ method, data, record_id }) => {
    const url = record_id ?
    constants.core_service_domain + '/' + serviceConfigsURLs.getClientCertificateConfigs + '/' + record_id :
    constants.core_service_domain + '/' + serviceConfigsURLs.getClientCertificateConfigs

    const request_data = data ? { url, method, data } : { url, method }

    const response = await request(request_data)

    console.log('client-certificate-configs', request_data, response)
  }

  const handleCertificatesUpdate = async (service_domains) => {
    if (certificateAction === 'generate') {
      for (let index = 0; index < adminUiCertificateJobs.length; index++) {
        const j = adminUiCertificateJobs[index]
        await clientCertificateConfigsRequest({ method: 'DELETE', record_id: j.job_id })
      }

      const postData = {
        ui_type: 'admin',
        generate_certificate: true,
        domains: service_domains,
      }

      await clientCertificateConfigsRequest({ method: 'POST', data: postData })
    } else if (certificateAction === 'upload') {
      let updated_job_ids = []
      let certificates_to_create = []

      for (let index = 0; index < adminUiCertificates.length; index++) {
        const c = adminUiCertificates[index]
        const certificate_domains = getDomainListFromCertificate(service_domains, c.domain)
        
        for (let index = 0; index < adminUiCertificateJobs.length; index++) {
          const j = adminUiCertificateJobs[index]
          const job_domains = getDomainListFromJobId(j.job_id)

          if (JSON.stringify(certificate_domains) === JSON.stringify(job_domains)) {
            const postData = {
              certificate: {
                domains: certificate_domains,
                expiration_date: getIsoDateTime(c.expire_date),
                certificate: c.certificate,
                private_key: c.private_key,
                chain: c.full_chain,
              }
            }

            await clientCertificateConfigsRequest({ method: 'PUT', data: postData, record_id: j.job_id })

            updated_job_ids.push(j.job_id)
          } else {
            certificates_to_create.push({
              domains: certificate_domains,
              expiration_date: getIsoDateTime(c.expire_date),
              certificate: c.certificate,
              private_key: c.private_key,
              chain: c.full_chain,
            })
          }
        }
      }

      if (certificates_to_create.length > 0) {
        const postData = {
          ui_type: 'admin',
          generate_certificate: false,
          certificates: certificates_to_create,
        }
  
        await clientCertificateConfigsRequest({ method: 'POST', data: postData })
      }      

      for (let index = 0; index < adminUiCertificateJobs.length; index++) {
        const j = adminUiCertificateJobs[index]

        if (!updated_job_ids.includes(j.job_id)) {
          await clientCertificateConfigsRequest({ method: 'DELETE', record_id: j.job_id })
        }        
      }
    }

    setCertificateAction('')    
  }

  const handleClickSave = async () => {
    setIsLoading(true)

    const form_valid = await validateForm() 

    if (form_valid.result) {
      let logo_url = ''

      if (adminUiLogo && typeof adminUiLogo !== 'string') {
        const logo_filename = await uploadFileRequest(adminUiLogo, 'getUploadUiLogo')
        if (logo_filename) {
          logo_url = logo_filename
        }
        if (adminUiCurrentLogo) {
          await deleteFileRequest(adminUiCurrentLogo)
        }
      } else if (adminUiLogo) {
        logo_url = adminUiLogo
      }
      
      let admin_ui_domains = []

      form_valid.admin_ui_domains.forEach(item => {
        admin_ui_domains.push(item.subdomain_prefix ? item.subdomain_prefix + '.' + item.domain_name : item.domain_name)
      })

      const postData = {
        config_params: {
          service_domain: admin_ui_domains,
          primary_color: adminUiPrimaryColor,
          secondary_color: adminUiSecondaryColor,
          logo: logo_url,
          captcha_config: {
            site_key: adminUiSiteKey,
            secret_key: adminUiSecretKey,
          },
        },
      }
      
      await clientServiceConfigsRequest(postData)

      await handleCertificatesUpdate(admin_ui_domains)

      setFetchServices(prev => prev + 1)
      setEditMode(false)
    } else {
      handleErrorDialogOpen()
    }

    setIsLoading(false) 
  }

  useEffect(() => {
    const verifySingleDomain = async (subdomain_prefix, domain_name) => {
      const url = constants.core_service_domain + '/' + serviceConfigsURLs.getClientServiceDnsResolver

      const postData = {
        domain_name,
        subdomain_prefix,
        type: 'admin',
      }

      const request_data = { url, method: 'POST', data: postData }    

      const response = await request(request_data)
      
      if (response && response.status_code < 400) {
        return 'verified'
      } else {
        return 'not verified'
      }
    }

    const fetchFullData = async () => {
      if (adminUiService && adminUiService.config_params && adminUiService.config_params.service_domain) {
        let domains = []
  
        for (let index = 0; index < adminUiService.config_params.service_domain.length; index++) {
          const item = adminUiService.config_params.service_domain[index]
          
          const domain_parts = item.split('.')
          const subdomain_prefix = domain_parts.slice(0, -2).join('.')
          const domain_name = domain_parts.slice(-2).join('.')
          const status = await verifySingleDomain(subdomain_prefix, domain_name)
          
          domains.push({
            subdomain_prefix,
            domain_name,
            status,
          })
        }
  
        setAdminUiDomain(domains)
      } else {
        setAdminUiDomain([{ ...uiDomainInitial }])
      }
  
      if (adminUiService && adminUiService.config_params && adminUiService.config_params.primary_color) {
        setAdminUiPrimaryColor(adminUiService.config_params.primary_color)
      } else {
        setAdminUiPrimaryColor(theme.palette.oriolesOrange)
      }
  
      if (adminUiService && adminUiService.config_params && adminUiService.config_params.secondary_color) {
        setAdminUiSecondaryColor(adminUiService.config_params.secondary_color)
      } else {
        setAdminUiSecondaryColor(theme.palette.mayGreen)
      }
  
      if (adminUiService && adminUiService.config_params && adminUiService.config_params.captcha_config && adminUiService.config_params.captcha_config.site_key) {
        setAdminUiSiteKey(adminUiService.config_params.captcha_config.site_key)
      } else {
        setAdminUiSiteKey('')
      }
  
      if (adminUiService && adminUiService.config_params && adminUiService.config_params.captcha_config && adminUiService.config_params.captcha_config.secret_key) {
        setAdminUiSecretKey(adminUiService.config_params.captcha_config.secret_key)
      } else {
        setAdminUiSecretKey('')
      }

      if (adminUiService && adminUiService.config_params && adminUiService.config_params.logo) {
        setAdminUiLogo(adminUiService.config_params.logo)
        setAdminUiCurrentLogo(adminUiService.config_params.logo)
      } else {
        setAdminUiLogo(null)
        setAdminUiCurrentLogo(null)
      }
    }

    fetchFullData()
  }, [adminUiService, theme])

  useEffect(() => {
    const fetchCertificates = async () => {
      const url = constants.core_service_domain + '/' + serviceConfigsURLs.getClientCertificateConfigs
      const request_data = { url, method: 'GET' }      
      const response = await request(request_data)
      
      if (response.status_code && response.status_code < 400 && response.data && response.data.length > 0) {
        setAdminUiCertificateJobs(response.data.filter(item => item.ui_type === 'admin'))      
      } else {
        setAdminUiCertificateJobs([])
      }
    }

    fetchCertificates()
  }, [])
  
  useEffect(() => {
    let domains_with_certificates = []

    adminUiDomain.forEach(d => {
      if (d.domain_name) {
        const domain_name = d.subdomain_prefix ? d.subdomain_prefix + '.' + d.domain_name : d.domain_name

        if (certificateAction === 'upload') {
          adminUiCertificates.forEach(c => {
            if (c.domain) {
              const is_wildcard_match = isWildcardMatch(c.domain, domain_name)
              if (is_wildcard_match) {
                domains_with_certificates.push(domain_name)
              }
            }
          })
        } else if (certificateAction === 'generate') {
          domains_with_certificates.push(domain_name)
        } else {
          const certificate_job = adminUiCertificateJobs.find(item => item.domain === domain_name)
        
          if (certificate_job) {
            if (certificate_job.cert_type === 'generate' || (certificate_job.status === 'ok' && getFormattedDateTime(certificate_job.expiration_date) > getFormattedDateTime())) {
              domains_with_certificates.push(domain_name)
            }
          }
        }        
      }
    })

    setDomainsWithCertificates(domains_with_certificates)
  }, [
    adminUiDomain,
    adminUiCertificateJobs,
    adminUiCertificates,
    certificateAction,
  ])
  
  return (
    <Box sx={wrapperStyle}>
      { isLoading && <CustomBackdrop open={isLoading} /> }

      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={headStyle}
      >
        <Box sx={labelStyle}>
          <Box>{defaultTexts.openStackAdminUi}</Box>
        </Box>
        <Box sx={{ paddingRight: '10px ' }}>
          {
            expanded ?
            <IconButton onClick={() => setExpanded(false)}>
              <FaMinimize color={theme.palette.greenSheen} />
            </IconButton> :
            <IconButton onClick={() => setExpanded(true)}>
              <FaMaximize color={theme.palette.greenSheen} />
            </IconButton>
          }
        </Box>
      </Stack>

      {
        expanded &&
        <Box sx={bodyStyle}>
          {
            editMode ?
            <AdminUiEdit
              adminUiDomain={adminUiDomain}
              setAdminUiDomain={setAdminUiDomain}
              adminUiCertificates={adminUiCertificates}
              setAdminUiCertificates={setAdminUiCertificates}
              adminUiSiteKey={adminUiSiteKey}
              setAdminUiSiteKey={setAdminUiSiteKey}
              adminUiSecretKey={adminUiSecretKey}
              setAdminUiSecretKey={setAdminUiSecretKey}
              adminUiLogo={adminUiLogo}
              setAdminUiLogo={setAdminUiLogo}
              adminUiPrimaryColor={adminUiPrimaryColor}
              setAdminUiPrimaryColor={setAdminUiPrimaryColor}
              adminUiSecondaryColor={adminUiSecondaryColor}
              setAdminUiSecondaryColor={setAdminUiSecondaryColor}
              domainsWithCertififcates={domainsWithCertififcates}
              certificateAction={certificateAction}
              setCertificateAction={setCertificateAction}
              verifyDomain={verifyDomain}
              errors={errors}
              validateFormFields={validateFormFields}
            /> :
            <AdminUiView
              { ...adminUiService }
              adminUiCertificateJobs={adminUiCertificateJobs}
            />
          }

          {
            editPermission &&
            <Stack
              direction='row'
              justifyContent='flex-end'
            >
              {
                editMode ?
                <Button
                  variant='contained'
                  disableRipple
                  disableElevation
                  disabled={false}
                  onClick={handleClickSave}
                  sx={saveButtonStyle}
                >
                  {defaultTexts.save}
                </Button> :
                <Button
                  variant='contained'
                  disableRipple
                  disableElevation
                  disabled={false}
                  onClick={handleClickEdit}
                  sx={editButtonStyle}
                >
                  {defaultTexts.edit}
                </Button>
              }            
            </Stack>
          }
        </Box>
      }

      <CustomDialog
        open={errorDialogOpen}
        onClose={handleErrorDialogClose}
        dialogTitle={{ title: defaultTexts.formValidationError }}
        dialogBody={{
          text: defaultTexts.formValidationErrorText,
          sx: { paddingBottom: '20px' }
        }}
      >
        {
          errors.map((item, index) => (
            <Box key={index}>{item.title}</Box>
          ))
        }
      </CustomDialog>
    </Box>
  )
}

export default AdminUiWrapper